import { ComponentType } from 'react'
import AreasStep from './steps/AreasStep'
import {
  InputDecodersStep,
  OutputDecodersStep,
  Sdi12DecodersStep,
} from './steps/DecodersStep'
import EtReferenceStep from './steps/EtReferenceStep'
import GeotagStep from './steps/GeotagStep'
import HydraulicStep from './steps/HydraulicStep'
import PlantSoilEmitterStep from './steps/PlantSoilEmitterStep'
import StationStep from './steps/StationStep'

/**
 * SCRUM-1032 — Guided Common Data setup.
 *
 * Registry of the guided-setup steps, in the order the user enters data. Every step component
 * receives the setup interface id (the URL `:id`, never AuthContext — SCRUM-976) and scopes all
 * data/save to it via the config grids' `interfaceId` override prop.
 */
export interface GuidedStepComponentProps {
  interfaceId: number
  /**
   * SCRUM-1032 — the wizard chrome (header, footer, step rail) is hidden and
   * the step owns the viewport. Steps that can use the extra room should
   * tighten their own padding to match.
   */
  focusMode?: boolean
  /** Toggles focus mode; steps surface it as a control in their own toolbar. */
  onToggleFocus?: () => void
}

export interface GuidedStep {
  key: string
  /** Translation key for the rail label — the rail and footer resolve it. */
  labelKey: string
  /** Ticket that implements this step's UI. */
  ticket: string
  Component: ComponentType<GuidedStepComponentProps>
}

// Order follows the datasets the user enters (grouped by screen). Plant/Soil/Emitter stays a single
// step that reuses the multi-column config grid.
const DATASET_STEPS: GuidedStep[] = [
  { key: 'hydraulic', labelKey: 'SetupStepHydraulic', ticket: 'SCRUM-1036', Component: HydraulicStep },
  { key: 'etReference', labelKey: 'etReference', ticket: 'SCRUM-1037', Component: EtReferenceStep },
  { key: 'plantSoilEmitter', labelKey: 'guidedSetupStepPlantSoilEmitter', ticket: 'SCRUM-1038', Component: PlantSoilEmitterStep },
  { key: 'areas', labelKey: 'menuStationConfigAreasSubAreas', ticket: 'SCRUM-1039', Component: AreasStep },
  { key: 'station', labelKey: 'SetupStepStation', ticket: 'SCRUM-1040', Component: StationStep },
  // Last, and deliberately after the stations exist: there is nothing to pin
  // on a map until they do.
  { key: 'geotag', labelKey: 'guidedSetupStepGeotagStations', ticket: 'SCRUM-1040', Component: GeotagStep },
]

const OUTPUT_STEP: GuidedStep = {
  key: 'decodersOutput',
  labelKey: 'controllersTabOutputDecoders',
  ticket: 'SCRUM-1035',
  Component: OutputDecodersStep,
}

const INPUT_STEP: GuidedStep = {
  key: 'decodersInput',
  labelKey: 'controllersTabInputDecoders',
  ticket: 'SCRUM-1035',
  Component: InputDecodersStep,
}

const SDI12_STEP: GuidedStep = {
  key: 'decodersSdi12',
  labelKey: 'controllersTabSdi12Decoders',
  ticket: 'SCRUM-1035',
  Component: Sdi12DecodersStep,
}

// Each decoder type is its own step — tabs inside a single Decoders step were
// easy to walk past, leaving a whole type unset. All three are always listed,
// including the types this controller has none of, so the list is the same
// shape everywhere and an empty step reads as "nothing to enter here".
export const GUIDED_STEPS: GuidedStep[] = [
  OUTPUT_STEP,
  INPUT_STEP,
  SDI12_STEP,
  ...DATASET_STEPS,
]
